/** Original synthesized placeholder audio. Replace with authored samples when available. */
import { AmbientMusic } from './ambientMusic';
type SoundName = 'tap' | 'slide' | 'exit' | 'blocked' | 'life' | 'hint' | 'complete' | 'fail' | 'button' | 'coin' | 'revive' | 'warning';
export type AudioPreferences = { music: boolean; sfx: boolean; haptics: boolean; musicVolume: number; sfxVolume: number };
export const DEFAULT_AUDIO_PREFS: AudioPreferences = { music: true, sfx: true, haptics: true, musicVolume: .25, sfxVolume: .7 };

/** Reads stored preferences; unknown or invalid fields keep their default. */
export function readAudioPrefs(data: unknown): AudioPreferences {
  const given = (typeof data === 'object' && data !== null ? data : {}) as Partial<Record<keyof AudioPreferences, unknown>>;
  const flag = (v: unknown, d: boolean) => typeof v === 'boolean' ? v : d;
  const level = (v: unknown, d: number) => typeof v === 'number' && v >= 0 && v <= 1 ? v : d;
  return {
    music: flag(given.music, DEFAULT_AUDIO_PREFS.music),
    sfx: flag(given.sfx, DEFAULT_AUDIO_PREFS.sfx),
    haptics: flag(given.haptics, DEFAULT_AUDIO_PREFS.haptics),
    musicVolume: level(given.musicVolume, DEFAULT_AUDIO_PREFS.musicVolume),
    sfxVolume: level(given.sfxVolume, DEFAULT_AUDIO_PREFS.sfxVolume),
  };
}

export interface AudioDeps {
  prefs: AudioPreferences;
  /** Stores changed preferences on the device. */
  savePrefs: (prefs: AudioPreferences) => void;
  /** Device vibration (native haptics, or the browser's vibrate). */
  vibrate: (pattern: number | number[]) => void;
}

export class AudioManager {
  prefs: AudioPreferences;
  private ctx: AudioContext | null = null;
  private music: AmbientMusic | null = null;
  private lastPlayed = new Map<SoundName, number>();
  private hidden = document.hidden;
  private readonly savePrefs: AudioDeps['savePrefs'];
  private readonly vibrate: AudioDeps['vibrate'];
  constructor(deps: AudioDeps) {
    this.prefs = { ...deps.prefs };
    this.savePrefs = deps.savePrefs;
    this.vibrate = deps.vibrate;
    document.addEventListener('visibilitychange', () => {
      this.hidden = document.hidden;
      if (this.hidden) this.stopMusic(); else this.startMusic();
    });
  }
  async unlock(): Promise<void> {
    if (!this.ctx) this.ctx = new AudioContext();
    if (this.ctx.state === 'suspended') await this.ctx.resume();
    this.startMusic();
  }
  set<K extends keyof AudioPreferences>(key: K, value: AudioPreferences[K]): void {
    this.prefs[key] = value;
    this.savePrefs({ ...this.prefs });
    if (key === 'music') { if (value) this.startMusic(); else this.stopMusic(); }
    if (key === 'musicVolume') this.music?.setVolume(this.prefs.musicVolume);
  }
  private beep(freq: number, duration: number, volume: number, type: OscillatorType = 'sine', offset = 0, endFreq?: number): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running' || this.hidden) return;
    const now = ctx.currentTime + offset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    if (endFreq) osc.frequency.exponentialRampToValueAtTime(Math.max(1, endFreq), now + duration);
    gain.gain.setValueAtTime(0.00001, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume * this.prefs.sfxVolume), now + Math.min(.025, duration / 4));
    gain.gain.exponentialRampToValueAtTime(.00001, now + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now); osc.stop(now + duration + .015);
  }
  play(name: SoundName): void {
    if (!this.prefs.sfx || this.hidden || !this.ctx) return;
    const now = performance.now();
    const limits: Partial<Record<SoundName, number>> = { tap: 40, slide: 80, exit: 70, warning: 700 };
    if (now - (this.lastPlayed.get(name) ?? -Infinity) < (limits[name] ?? 10)) return;
    this.lastPlayed.set(name, now);
    const subtle = .97 + Math.random() * .06;
    switch (name) {
      case 'tap': this.beep(560 * subtle, .055, .055, 'sine', 0, 450); break;
      case 'slide': this.beep(680 * subtle, .22, .045, 'triangle', 0, 300); break;
      case 'exit': this.beep(420 * subtle, .085, .072, 'sine', 0, 610); break;
      case 'blocked': this.beep(170, .10, .10, 'triangle', 0, 145); this.beep(150, .095, .08, 'triangle', .11, 120); break;
      case 'life': this.beep(310, .18, .085, 'sine', 0, 190); break;
      case 'hint': this.beep(700, .22, .08); this.beep(1030, .35, .055, 'sine', .12); break;
      case 'complete': [660, 830, 990].forEach((n, i) => this.beep(n, .29, .10, 'sine', i * .13)); break;
      case 'fail': this.beep(350, .26, .09, 'sine', 0, 230); this.beep(245, .31, .075, 'sine', .20, 160); break;
      case 'button': this.beep(590, .055, .045, 'sine'); break;
      case 'coin': this.beep(930, .13, .07); this.beep(1210, .2, .05, 'sine', .075); break;
      case 'revive': this.beep(460, .20, .09); this.beep(750, .3, .07, 'sine', .12); break;
      case 'warning': this.beep(620, .10, .045); break;
    }
  }
  haptic(pattern: number | number[]): void {
    if (!this.prefs.haptics || this.hidden) return;
    this.vibrate(pattern);
  }
  startMusic(): void {
    const ctx = this.ctx;
    if (!ctx || this.hidden || !this.prefs.music || ctx.state !== 'running') return;
    this.music ??= new AmbientMusic(ctx);
    this.music.start(this.prefs.musicVolume);
  }
  stopMusic(): void {
    this.music?.stop();
  }
}
